import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, User, LogOut, AlertCircle, Settings, ShieldCheck, Menu, X, Calendar, Wallet } from 'lucide-react';
import GeneralNotificationBell from './GeneralNotificationBell';
import RaiseIssueModal from './RaiseIssueModal';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { useTranslation } from 'react-i18next';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRaiseIssueModalOpen, setIsRaiseIssueModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Detect screen size to conditionally render notification bell
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-black shadow-sm border-b border-gray-800 sticky top-0 z-40 w-full">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center py-2 sm:py-2 md:py-3">
          {/* Logo */}
          <div className="flex items-center min-w-0 flex-shrink space-x-2">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/allavail-icon.png"
                alt="AllAvail" 
                className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 flex-shrink-0 object-contain"
              />
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate">
                AllAvail
              </span>
            </Link>
          </div>

          {/* Desktop Navigation - Hidden on mobile */}
          {isAuthenticated && (
            <nav className="hidden lg:flex items-center space-x-3 xl:space-x-6 2xl:space-x-8">
              {/* Unified Notifications (includes driver requests and general notifications) - Desktop only */}
              {!isMobile && <GeneralNotificationBell />}

              {/* Role-based navigation */}
              {user?.role === 'customer' && (
                <Link to="/bookings" className="text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                  {t('menu.myBookings')}
                </Link>
              )}
              
              {user?.role === 'provider' && (
                <>
                  {/* Show different links for drivers vs other providers */}
                  {!user?.skills?.includes('driver') && (
                    <Link to="/schedules" className="text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                      {t('menu.mySchedules')}
                    </Link>
                  )}
                  {user?.skills?.includes('driver') && (
                    <Link to="/driver-earnings" className="text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                      {t('menu.earnings')}
                    </Link>
                  )}
                  <Link to="/provider-bookings" className="text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                    {t('menu.myBookings')}
                  </Link>
                </>
              )}
              
              {user?.role === 'admin' && (
                <>
                  <Link to="/admin" className="text-xs lg:text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                    {t('menu.dashboard')}
                  </Link>
                  <Link to="/bookings" className="text-xs lg:text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                    {t('menu.myBookings')}
                  </Link>
                </>
              )}

              {/* User Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-1 lg:space-x-2 text-gray-300 hover:text-white transition-colors"
                >
                  <div className="text-left">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium truncate max-w-[120px] xl:max-w-none">{user?.name}</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 lg:py-1 rounded-full capitalize">
                        {user?.role}
                      </span>
                    </div>
                    {FEATURE_FLAGS.KYC_ENABLED && (
                      <>
                        {user?.kycStatus === 'approved' ? (
                          <div className="flex items-center mt-0.5">
                            <ShieldCheck className="h-3 w-3 text-green-600 mr-1" />
                            <span className="text-xs text-green-600 font-medium">{t('menu.verified')}</span>
                          </div>
                        ) : user?.kycStatus === 'pending' ? (
                          <div className="flex items-center mt-0.5">
                            <AlertCircle className="h-3 w-3 text-yellow-600 mr-1" />
                            <span className="text-xs text-yellow-600">{t('menu.kycPending')}</span>
                          </div>
                        ) : user?.kycStatus === 'rejected' ? (
                          <div className="flex items-center mt-0.5">
                            <AlertCircle className="h-3 w-3 text-red-600 mr-1" />
                            <span className="text-xs text-red-600">{t('menu.kycRejected')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center mt-0.5">
                            <ShieldCheck className="h-3 w-3 text-gray-400 mr-1" />
                            <span className="text-xs text-gray-500">{t('menu.notKycVerified')}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <ChevronDown 
                    className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} 
                  />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-3" />
                      {t('menu.profile')}
                    </Link>
                    
                    {/* KYC Verification Link - for all users (Feature Flag Controlled) */}
                    {FEATURE_FLAGS.KYC_ENABLED && (
                      <Link
                        to="/kyc"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <ShieldCheck className="h-4 w-4 mr-3" />
                        <div className="flex items-center justify-between flex-1">
                          <span>{t('menu.kycVerification')}</span>
                          {user?.kycStatus === 'approved' ? (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              {t('menu.verified')}
                            </span>
                          ) : user?.kycStatus === 'pending' ? (
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                              {t('menu.pending')}
                            </span>
                          ) : user?.kycStatus === 'rejected' ? (
                            <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                              {t('menu.rejected')}
                            </span>
                          ) : (
                            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {t('menu.notKycVerified')}
                            </span>
                          )}
                        </div>
                      </Link>
                    )}
                    
                    {/* Admin specific options */}
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin/notification-settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        {t('menu.notificationSettings')}
                      </Link>
                    )}
                    
                    {/* Customer and Provider options */}
                    {(user?.role === 'customer' || user?.role === 'provider') && (
                      <>
                        <Link
                          to="/my-complaints"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <AlertCircle className="h-4 w-4 mr-3" />
                          {t('menu.myComplaints')}
                        </Link>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            setIsRaiseIssueModalOpen(true);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <AlertCircle className="h-4 w-4 mr-3" />
                          {t('menu.raiseIssue')}
                        </button>
                      </>
                    )}
                    
                    <div className="border-t border-gray-200 my-1"></div>
                    
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      {t('menu.logout')}
                    </button>
                  </div>
                )}
              </div>
            </nav>
          )}

          {/* Mobile Menu Button and Actions */}
          {isAuthenticated && (
            <div className="flex items-center space-x-1 sm:space-x-2 lg:hidden flex-shrink-0">
              {/* Mobile Notifications - Mobile only */}
              {isMobile && <GeneralNotificationBell />}
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-1.5 sm:p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 transition-colors flex-shrink-0"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
              </button>
            </div>
          )}

          {/* Login Link for unauthenticated users */}
          {!isAuthenticated && (
            <Link 
              to="/login" 
              className="bg-white text-black px-4 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors text-sm sm:text-base whitespace-nowrap flex-shrink-0"
            >
              {t('menu.signIn')}
            </Link>
          )}
        </div>

        {/* Mobile Navigation Menu - Content styled like Android, but keeping dropdown layout */}
        {isAuthenticated && isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-800 py-2 sm:py-3 space-y-1 max-h-[calc(100vh-60px)] overflow-y-auto bg-gradient-to-r from-[#2C2C2C] to-[#3A3A3A]">
            {/* Profile Header - Similar to Android */}
            <div className="px-3 sm:px-4 py-4 sm:py-5 mb-2">
              <div className="flex items-center">
                {user?.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.name}
                    className="w-14 h-14 rounded-full object-cover mr-3 flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-700 flex items-center justify-center mr-3 flex-shrink-0">
                    <User className="h-8 w-8 text-gray-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-white truncate">{user?.name || 'User'}</p>
                  <p className="text-sm text-gray-300 capitalize">
                    {user?.role === 'customer' 
                      ? t('menu.customer')
                      : user?.role === 'provider' 
                      ? t('menu.serviceProvider')
                      : user?.role 
                      ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                      : 'User'}
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-gray-800 mx-3 sm:px-4 my-2" />

            {/* Menu Items - Similar to Android styling */}
            <div className="px-3 sm:px-4">
              {/* My Bookings - Always shown first like Android */}
              <Link
                to={user?.role === 'provider' ? '/provider-bookings' : '/bookings'}
                className="flex items-center py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Calendar className="h-5 w-5 mr-3 text-gray-500 flex-shrink-0" />
                <span className="text-base">My Bookings</span>
              </Link>

              {/* Profile */}
              <Link
                to="/profile"
                className="flex items-center py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <User className="h-5 w-5 mr-3 text-gray-300 flex-shrink-0" />
                <span className="text-base">Profile</span>
              </Link>

              {/* Earnings - For drivers */}
              {user?.role === 'provider' && user?.skills?.includes('driver') && (
                <Link
                  to="/driver-earnings"
                  className="flex items-center py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Wallet className="h-5 w-5 mr-3 text-gray-300 flex-shrink-0" />
                  <span className="text-base">Earnings</span>
                </Link>
              )}

              {/* My Complaints - For customers and providers */}
              {(user?.role === 'customer' || user?.role === 'provider') && (
                <>
                  <Link
                    to="/my-complaints"
                    className="flex items-center py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <AlertCircle className="h-5 w-5 mr-3 text-gray-300 flex-shrink-0" />
                    <span className="text-base">My Complaints</span>
                  </Link>

                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsRaiseIssueModalOpen(true);
                    }}
                    className="flex items-center w-full text-left py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                  >
                    <AlertCircle className="h-5 w-5 mr-3 text-gray-300 flex-shrink-0" />
                    <span className="text-base">Raise an Issue</span>
                  </button>
                </>
              )}

              {/* Admin specific items */}
              {user?.role === 'admin' && (
                <>
                  <Link
                    to="/admin"
                    className="flex items-center py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Settings className="h-5 w-5 mr-3 text-gray-300 flex-shrink-0" />
                    <span className="text-base">Admin Dashboard</span>
                  </Link>
                  <Link
                    to="/admin/notification-settings"
                    className="flex items-center py-4 border-b border-gray-800 text-white hover:text-gray-300 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Settings className="h-5 w-5 mr-3 text-gray-300 flex-shrink-0" />
                    <span className="text-base">Notification Settings</span>
                  </Link>
                </>
              )}

              {/* Logout - Styled like Android (red, with top border) */}
              <div className="mt-2 pt-4 border-t border-gray-800">
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center w-full text-left py-4 text-red-500 hover:text-red-400 transition-colors"
                >
                  <LogOut className="h-5 w-5 mr-3 text-red-500 flex-shrink-0" />
                  <span className="text-base font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Raise Issue Modal */}
      <RaiseIssueModal
        isOpen={isRaiseIssueModalOpen}
        onClose={() => setIsRaiseIssueModalOpen(false)}
      />
    </header>
  );
};

export default Header;
